import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { MdMoreHoriz } from 'react-icons/md';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Styled from './view-edit-delete-style';

/**
 * ViewEditDelete component for generic use in Table component.
 * @param {object} props The props object.
 * @param {Function} props.onView The view function.
 * @param {Function} props.onEdit The edit function.
 * @param {Function} props.onDelete The delete function.
 * @param {string} props.feature The feature name.
 * @param {Function} props.onDuplicate The duplicate function.
 * @param {string} props.viewText The view text.
 * @param {string} props.menuPlacement The menu placement ('top' or 'bottom').
 * @returns {JSX.Element}
 */
const ViewEditDelete = ({
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  feature,
  viewText,
  menuPlacement,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupContainerRef = useRef(null);
  const popupMenuRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const calculateHorizontalPosition = (buttonRect, estimatedMenuWidth) => {
    const spaceOnRight = window.innerWidth - buttonRect.right;
    const spaceOnLeft = buttonRect.left;

    if (spaceOnRight >= estimatedMenuWidth) {
      return { alignment: 'left', x: buttonRect.right };
    }
    if (spaceOnLeft >= estimatedMenuWidth) {
      return { alignment: 'right', x: window.innerWidth - buttonRect.left };
    }
    // Fallback: position to the right but allow overflow
    return { alignment: 'left', x: buttonRect.right };
  };

  const calculateVerticalPosition = (buttonRect, estimatedMenuHeight) => {
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    if (menuPlacement === 'top') {
      return { y: buttonRect.top, verticalPlacement: 'top' };
    }
    if (spaceBelow >= estimatedMenuHeight) {
      return { y: buttonRect.bottom, verticalPlacement: 'bottom' };
    }
    if (spaceAbove >= estimatedMenuHeight) {
      return { y: buttonRect.top, verticalPlacement: 'top' };
    }
    // Not enough space either way - default to bottom and allow overflow
    return { y: buttonRect.bottom, verticalPlacement: 'bottom' };
  };

  const togglePopup = (e) => {
    if (!isPopupOpen) {
      const buttonRect = e.target.getBoundingClientRect();
      const estimatedMenuWidth = 150; // Conservative estimate
      const estimatedMenuHeight = 160; // Conservative estimate for menu height

      const { alignment, x } = calculateHorizontalPosition(
        buttonRect,
        estimatedMenuWidth,
      );
      const { y, verticalPlacement } = calculateVerticalPosition(
        buttonRect,
        estimatedMenuHeight,
      );

      setPosition({ x, y, alignment, verticalPlacement });
      setIsPopupOpen(true);
    } else {
      setIsPopupOpen(false);
    }
  };

  const handleItemClick = () => {
    setIsPopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Styled.PopupContainer ref={popupContainerRef}>
      <Styled.IconButton type="button" onClick={togglePopup}>
        <MdMoreHoriz />
      </Styled.IconButton>
      {isPopupOpen && (
        <Styled.PopupMenu ref={popupMenuRef} $position={position}>
          {onView && (
            <PermissionWrapper permissions={[`${feature}.read`]}>
              <Styled.MenuItem
                onClick={() => {
                  onView();
                  handleItemClick();
                }}
              >
                <Styled.ViewIcon />
                <Styled.Text>{viewText ?? 'View'}</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onDuplicate && (
            <PermissionWrapper permissions={[`${feature}.create`]}>
              <Styled.MenuItem
                onClick={() => {
                  onDuplicate();
                  handleItemClick();
                }}
              >
                <Styled.DuplicateIcon />
                <Styled.Text>Duplicate</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onEdit && (
            <PermissionWrapper permissions={[`${feature}.update`]}>
              <Styled.MenuItem
                onClick={() => {
                  onEdit();
                  handleItemClick();
                }}
              >
                <Styled.EditIcon />
                <Styled.Text>Edit</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onDelete && (
            <PermissionWrapper permissions={[`${feature}.delete`]}>
              <Styled.MenuItem
                onClick={() => {
                  onDelete();
                  handleItemClick();
                }}
              >
                <Styled.DeleteIcon />
                <Styled.Red>Delete</Styled.Red>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
        </Styled.PopupMenu>
      )}
    </Styled.PopupContainer>
  );
};

ViewEditDelete.propTypes = {
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onView: PropTypes.func,
  feature: PropTypes.string.isRequired,
  viewText: PropTypes.string,
  menuPlacement: PropTypes.oneOf(['top', 'bottom']),
};

ViewEditDelete.defaultProps = {
  onDelete: () => {},
  onEdit: () => {},
  onView: () => {},
  onDuplicate: () => {},
  viewText: undefined,
  menuPlacement: 'bottom',
};

export default ViewEditDelete;
